import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';

import { individualsService, DeleteIndividualRequest, useToast } from '@aslanpay/portal-common-lib';

import humanizeString from 'humanize-string';

import { IndividualType } from 'app/components/UI/Table/Table.types';

const getApiErrorMessage = (error: unknown) =>
  axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined;

const useDeleteIndividuals = (
  type: IndividualType,
  { onSuccess, onError }: UseMutationOptions<unknown, Error, DeleteIndividualRequest[]>
) => {
  const toast = useToast();

  return useMutation<unknown, Error, DeleteIndividualRequest[]>(
    async (ids: DeleteIndividualRequest[]) => {
      const deletePromises = ids.map(({ id }) => individualsService.deleteIndividual(id));
      const results = await Promise.allSettled(deletePromises);

      const failures = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );
      const allSuccess = failures.length === 0;
      const allFailed = !allSuccess && failures.length === results.length;
      const someFailed = !allSuccess && !allFailed;
      const failureToReport =
        failures.find((failure) => getApiErrorMessage(failure.reason)) ?? failures[0];

      const description = ids.length > 1 ? `${type}s were` : `${type} was`;

      if (allSuccess) {
        toast({
          position: 'top-right',
          status: 'success',
          title: `${humanizeString(type)}${ids.length > 1 ? 's' : ''} deleted`,
          description: `${ids.length} ${description} successfully deleted!`,
        });
      }
      if (someFailed) {
        toast({
          position: 'top-right',
          status: 'warning',
          title: `Some ${type}s were not deleted`,
          description:
            getApiErrorMessage(failureToReport.reason) ||
            `Sorry, some of the ${type}s were not deleted. Please try again.`,
        });
      }
      if (allFailed) {
        throw failureToReport.reason;
      }
    },
    {
      onSuccess: (data, variables, context) => {
        if (onSuccess) onSuccess(data, variables, context);
      },
      onError: (error, variables, context) => {
        const description = variables.length > 1 ? `${type}s were` : `${type} was`;

        toast({
          position: 'top-right',
          status: 'error',
          title:
            variables.length > 1
              ? `None of the ${variables.length} ${type}s were deleted`
              : 'Something went wrong',
          description:
            getApiErrorMessage(error) || `Sorry, the ${description} not deleted. Please try again.`,
        });

        if (onError) onError(error, variables, context);
      },
    }
  );
};

export default useDeleteIndividuals;
